import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Heart, Sparkles, Video, Clock, Users, Star, Shield, Zap, MessageSquare, MessageCircle, BarChart3, Trophy, Target, Crown, Flame, Share2, Bell, LogOut, Menu, X } from 'lucide-react';
import CreditBalance from './CreditBalance';
import { motion } from 'framer-motion';
import EnhancedButton from './EnhancedButton';
import { HomepageAd } from './AdPlacements';
import { LoadingSpinner } from './LoadingStates';
import { LiveUserCounter, StatsCard, TestimonialCard } from './InteractiveComponents';
import ShareButton from './ShareButton';
import ReferralShareButton from './ReferralShareButton';
import InstallButton from './InstallButton';
import { AccessibleButton, AccessibleImage } from './AccessibilityComponents';
import './ui/animations.css';
import SEOHead from './SEOHead';

const EnhancedHomepage = () => {
  const navigate = useNavigate();
  const { logout, token } = useContext(AuthContext);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Simulate loading for enhanced animations
    setTimeout(() => setIsLoaded(true), 500);
  }, []);

  const testimonials = [
    {
      name: "Sarah",
      age: 28,
      testimonial: "Found my perfect match in just 3 sessions! The AI icebreakers really work.",
      rating: 5,
      verified: true
    },
    {
      name: "Mike",
      age: 32,
      testimonial: "Beat the shot clock and found love! This app makes dating actually fun.",
      rating: 5,
      verified: true
    },
    {
      name: "Emily",
      age: 26,
      testimonial: "The time pressure made conversations so much more genuine and exciting.",
      rating: 4,
      verified: true
    }
  ];

  const achievements = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "First Match",
      description: "Made your first connection",
      unlocked: true
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Speed Demon",
      description: "Complete 10 speed dates",
      progress: 60
    },
    {
      icon: <Trophy className="w-6 h-6" />,
      title: "Conversation Master",
      description: "Use 50 AI icebreakers",
      progress: 30
    }
  ];

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-x-hidden">
        <LoadingSpinner text="Loading your perfect match experience..." size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <SEOHead 
        title="Shot Clock Dating - World's First AI-Powered Speed Dating Platform | Find Love in Minutes"
        description="Join millions connecting through AI-powered speed dating. Experience timed video conversations with compatible singles. Start your journey to meaningful connections today - it's free to begin!"
        keywords="speed dating, AI dating, video dating, online dating, matchmaking, timed conversations, AI powered dating, dating app, find love, compatible singles, intelligent matchmaking, virtual speed dating, dating platform, love connection, relationship app, meet singles online, dating website, video chat dating, smart dating, modern dating"
        url="https://shotclockdating.com/"
        image="https://shotclockdating.com/og-homepage.jpg"
      />

      {/* Top Half - Romantic Couple Background Image */}
      <div 
        className="absolute top-0 left-0 right-0 h-1/2 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.75), rgba(30, 41, 59, 0.7)), url('https://customer-assets.emergentagent.com/job_shotclock-dating-1/artifacts/ptct56ee_08.jpg')`,
        }}
      ></div>
      
      {/* Bottom Half - Elegant Gradient Background */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-b from-slate-800 via-slate-900 to-slate-900"
      ></div>
      
      {/* Animated Gradient Overlay */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-r from-rose-500/20 via-transparent to-purple-500/20 animate-gradient-x"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-500/10 to-transparent animate-gradient-y" style={{animationDelay: '2s'}}></div>
      </div>
      
      {/* Elegant Grid Pattern Overlay with Movement */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="w-full h-full animate-float"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '24px 24px',
            animationDuration: '20s'
          }}
        ></div>
      </div>

      {/* Floating Animated Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large Floating Orbs */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-rose-500/10 to-pink-500/10 rounded-full blur-xl animate-float-slow"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-full blur-xl animate-float-medium" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-32 left-32 w-40 h-40 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-full blur-xl animate-float-slow" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-60 left-1/2 w-28 h-28 bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-full blur-xl animate-float-medium" style={{animationDelay: '3s'}}></div>
        <div className="absolute bottom-40 right-40 w-36 h-36 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-full blur-xl animate-float-slow" style={{animationDelay: '4s'}}></div>
        
        {/* Small Floating Hearts */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute text-rose-400/20 animate-float-heart"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.8}s`,
              animationDuration: `${8 + Math.random() * 4}s`,
              fontSize: `${Math.random() * 15 + 15}px`
            }}
          >
            ♥
          </div>
        ))}
        
        {/* Animated Light Rays */}
        <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-transparent via-rose-400/10 to-transparent animate-pulse" style={{animationDuration: '4s'}}></div>
        <div className="absolute top-0 right-1/3 w-1 h-full bg-gradient-to-b from-transparent via-purple-400/10 to-transparent animate-pulse" style={{animationDuration: '5s', animationDelay: '1s'}}></div>
        <div className="absolute top-0 left-2/3 w-1 h-full bg-gradient-to-b from-transparent via-blue-400/10 to-transparent animate-pulse" style={{animationDuration: '6s', animationDelay: '2s'}}></div>
        
        {/* Moving Geometric Shapes */}
        <div className="absolute top-1/4 left-0 w-2 h-2 bg-rose-400/30 transform rotate-45 animate-move-diagonal"></div>
        <div className="absolute top-1/3 right-0 w-3 h-3 bg-purple-400/30 rounded-full animate-move-horizontal" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-1/4 left-0 w-2 h-6 bg-blue-400/30 animate-move-vertical" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-1/3 right-0 w-4 h-1 bg-pink-400/30 animate-move-horizontal" style={{animationDelay: '3s'}}></div>
        
        {/* Twinkling Stars */}
        {[...Array(20)].map((_, i) => (
          <div
            key={`star-${i}`}
            className="absolute w-1 h-1 bg-white/40 rounded-full animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          ></div>
        ))}
      </div>

      <div className="relative z-10 min-h-screen">
        {/* Sophisticated Hero Section */}
        <div className="pt-32 pb-16 px-6 sm:px-8 lg:px-12">
          <div className="text-center max-w-7xl mx-auto">

            {/* Logo Section with Enhanced Size and Clarity */}
            <div className="flex flex-col justify-center items-center mb-12 animate-scaleIn">
              <div className="relative group mb-4 w-full flex justify-center">
                <AccessibleImage 
                  src="/shot-clock-logo.png" 
                  alt="Shot Clock Dating Logo - World's First AI-Powered Speed Dating Platform with Heart-Clock Design"
                  className="h-48 w-48 sm:h-52 sm:w-52 md:h-56 md:w-56 lg:h-60 lg:w-60 object-contain transform group-hover:scale-105 transition-all duration-700 max-w-none"
                  style={{ 
                    imageRendering: '-webkit-optimize-contrast',
                    imageRendering: 'crisp-edges',
                    filter: 'contrast(1.15) saturate(1.2) brightness(1.05)',
                    width: '192px !important',
                    height: '192px !important',
                    minWidth: '192px',
                    minHeight: '192px',
                    WebkitImageRendering: '-webkit-optimize-contrast',
                    msImageRendering: '-ms-interpolation-mode: bicubic'
                  }}
                  onError={(e) => {
                    console.error('Logo failed to load:', e);
                    // Fallback to original if shot-clock-logo.png fails
                    e.target.src = "/original_logo.png";
                  }}
                  onLoad={() => {
                    console.log('Homepage logo loaded successfully');
                  }}
                />
              </div>
              {/* Clear subtitle under logo */}
              <p className="text-lg md:text-xl text-gray-300 font-light tracking-wide text-center">
                Connect with compatible singles through timed video conversations
              </p>
            </div>

            {/* Updated Direct Headline */}
            <div className="mb-16 animate-fadeInUp stagger-2">
              <h1 className="text-3xl md:text-5xl font-light text-white mb-8 tracking-tight leading-none text-center">
                <span className="block font-light">The World's First</span>
                <span className="block font-semibold bg-gradient-to-r from-rose-400 via-pink-400 to-rose-500 bg-clip-text text-transparent">
                  AI-Powered Speed Dating Platform
                </span>
              </h1>
              
              <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-rose-400 to-transparent mx-auto mb-8 opacity-60" 
                   role="presentation" aria-hidden="true"></div>
              
              <p className="text-lg md:text-xl text-gray-300 max-w-6xl mx-auto leading-relaxed font-light tracking-wide text-center">
                <span className="sm:whitespace-nowrap">Experience the sophisticated art of connection through curated <span className="text-rose-300 font-medium">Speed Dates</span>,</span>
                <br className="hidden md:block" />
                where every conversation is <span className="text-white font-medium">thoughtfully designed</span> to spark genuine connections.
              </p>
            </div>

            {/* Premium CTA Section */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-6 sm:space-y-0 sm:space-x-8 mb-20 animate-slideInUp stagger-3">
              <AccessibleButton 
                onClick={() => {
                  // Smart routing based on authentication status
                  if (token) {
                    // User is logged in - take them to matches page
                    navigate('/matches');
                  } else {
                    // User is not logged in - take them to register page
                    navigate('/register');
                  }
                }}
                size="lg"
                className="animate-heartbeat bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 px-8 py-4 text-xl font-semibold !rounded-2xl text-white flex items-center justify-center transition-transform hover:scale-105"
                ariaLabel={token ? "Start a speed date with your matches" : "Sign up to start your speed dating journey"}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <Heart 
                      className="w-10 h-10 text-white animate-pulse" 
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    />
                  </div>
                  <span>Start Your Speed Date</span>
                </div>
              </AccessibleButton>
              
              <AccessibleButton 
                onClick={() => navigate('/how-it-works')}
                size="lg"
                variant="ghost"
                className="border-white/30 text-white hover:bg-white hover:text-gray-900 backdrop-blur-sm px-6 py-3 text-lg font-semibold !rounded-2xl border bg-transparent flex items-center justify-center"
                ariaLabel="Learn how Shot Clock Dating works"
              >
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 text-yellow-400" aria-hidden="true" />
                  <span>Discover How</span>
                </div>
              </AccessibleButton>

              <AccessibleButton 
                onClick={() => navigate('/advanced-features')}
                size="lg"  
                variant="ghost"
                className="hover-tilt border border-purple-400/50 text-purple-300 hover:bg-purple-400/10 hover:border-purple-400 backdrop-blur-sm px-6 py-3 text-lg font-semibold !rounded-2xl flex items-center justify-center"
                ariaLabel="Explore advanced features and capabilities"
              >
                <div className="flex items-center space-x-2">
                  <span aria-hidden="true">🔥</span>
                  <span>Explore Advanced Features</span>
                </div>
              </AccessibleButton>
            </div>

            {/* Sophisticated Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20 animate-fadeInUp stagger-4">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover-lift">
                <StatsCard 
                  icon={<Users className="w-3 h-3 sm:w-4 sm:h-4" />}
                  label="Active Members"
                  value="10K+"
                  trend={{ direction: 'up', value: 15 }}
                  color="blue"
                />
              </div>
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover-lift">
                <StatsCard 
                  icon={<Heart className="w-3 h-3 sm:w-4 sm:h-4" />}
                  label="Successful Matches"
                  value="2.5K+"
                  trend={{ direction: 'up', value: 23 }}
                  color="rose"
                />
              </div>
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover-lift">
                <StatsCard 
                  icon={<MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />}
                  label="Conversations"
                  value="50K+"
                  trend={{ direction: 'up', value: 8 }}
                  color="green"
                />
              </div>
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover-lift">
                <StatsCard 
                  icon={<Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />}
                  label="Success Rate"
                  value="78%"
                  trend={{ direction: 'up', value: 5 }}
                  color="purple"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Refined Features Section */}
        <div className="bg-gradient-to-b from-black/20 via-black/40 to-black/60 backdrop-blur-xl py-32 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-24 animate-fadeInUp">
              <h2 className="text-5xl md:text-6xl font-light text-white mb-6 tracking-tight">
                Crafted for 
                <span className="font-semibold bg-gradient-to-r from-rose-400 to-pink-400 bg-clip-text text-transparent"> Excellence</span>
              </h2>
              <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-rose-400 to-transparent mx-auto opacity-60"></div>
              <p className="text-xl text-gray-400 mt-8 max-w-3xl mx-auto font-light">
                Every detail thoughtfully designed to elevate your dating experience
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-12">
              {/* Enhanced Feature Cards */}
              {[
                {
                  icon: <Video className="w-12 h-12" />,
                  title: "Premium Video Experience",
                  description: "Crystal clear HD video calls designed for authentic, meaningful conversations that spark genuine connections.",
                  delay: "stagger-1",
                  gradient: "from-blue-500/20 to-cyan-500/20",
                  iconColor: "text-blue-400"
                },
                {
                  icon: <Sparkles className="w-12 h-12" />,
                  title: "AI-Powered Intelligence",
                  description: "Sophisticated conversation coaching and compatibility analysis powered by advanced AI to help you connect naturally.",
                  delay: "stagger-2",
                  gradient: "from-purple-500/20 to-pink-500/20",
                  iconColor: "text-purple-400"
                },
                {
                  icon: <Users className="w-12 h-12" />,
                  title: "Intelligent Matching",
                  description: "Advanced compatibility algorithm that learns your preferences and connects you with your ideal matches.",
                  delay: "stagger-3",
                  gradient: "from-rose-500/20 to-orange-500/20",
                  iconColor: "text-rose-400"
                }
              ].map((feature, index) => (
                <div key={index} className={`group text-center animate-fadeInUp ${feature.delay}`}>
                  <div className="relative mb-8">
                    <div className={`w-28 h-28 bg-gradient-to-br ${feature.gradient} backdrop-blur-xl rounded-3xl mx-auto flex items-center justify-center group-hover:scale-110 transition-all duration-700 border border-white/10 hover-lift shadow-2xl`}>
                      <div className={`${feature.iconColor} group-hover:scale-110 transition-transform duration-500`}>
                        {feature.icon}
                      </div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-6 group-hover:text-rose-200 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-lg leading-relaxed font-light max-w-sm mx-auto">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sophisticated Social Proof Section */}
        <div className="bg-gradient-to-b from-black/60 to-black/80 backdrop-blur-xl py-24 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16 animate-fadeInUp">
              <h2 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-tight">
                Real Stories, Real <span className="font-semibold text-rose-400">Connections</span>
              </h2>
              <p className="text-gray-400 text-lg font-light">Discover what our community is saying</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className={`animate-slideInUp stagger-${index + 1}`}>
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover-lift">
                    <TestimonialCard {...testimonial} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Homepage Ad for Free Users */}
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="animate-fadeInUp">
            <HomepageAd className="mx-auto max-w-2xl bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10" />
          </div>
        </div>

        {/* Premium Footer Section */}
        <div className="bg-gradient-to-t from-black/90 to-transparent backdrop-blur-xl py-24 border-t border-white/10">
          <div className="max-w-5xl mx-auto text-center px-6">
            <h3 className="text-4xl md:text-5xl font-light text-white mb-8 tracking-tight animate-fadeInUp">
              Ready to write your 
              <span className="font-semibold bg-gradient-to-r from-rose-400 to-pink-400 bg-clip-text text-transparent"> love story</span>?
            </h3>
            <p className="text-xl text-gray-400 mb-8 font-light max-w-3xl mx-auto leading-relaxed animate-fadeInUp stagger-1">
              Join an exclusive community where every conversation has the potential to change your life forever.
            </p>
            
            {/* PWA Install Button */}
            <div className="flex justify-center mb-8 animate-slideInUp stagger-1">
              <InstallButton variant="primary" className="transform hover:scale-105 transition-all duration-300" />
            </div>
            
            {/* Enhanced Share Section with Instructions */}
            <div className="flex justify-center mb-12 animate-slideInUp stagger-2">
              <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/20 shadow-lg max-w-md mx-auto">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-3 animate-pulse">
                    <Share2 className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">
                    🎉 Love Shot Clock Dating?
                  </h4>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    Help your friends find love too! Share our app and both get $5 credits when they complete their profile.
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <ShareButton 
                    className="text-white flex-1 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 rounded-xl px-6 py-3 font-medium transition-all duration-300 hover:scale-105 shadow-lg" 
                    title="Shot Clock Dating - Find Your Perfect Match!"
                    text="🔥 AI-powered speed dating with video calls! Join me on the most sophisticated dating platform. Real connections, real results! 💕"
                  />
                  <ReferralShareButton />
                </div>
                
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-400">
                    ✨ <span className="text-rose-300">Bonus:</span> You both get $5 credit when friends complete signup!
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-6 animate-slideInUp stagger-3">
              <EnhancedButton 
                onClick={() => {
                  // Smart routing based on authentication status
                  if (token) {
                    // User is logged in - take them to matches page
                    navigate('/matches');
                  } else {
                    // User is not logged in - take them to register page
                    navigate('/register');
                  }
                }}
                size="xl"
                variant="primary"
                className="hover-lift animate-heartbeat shadow-2xl shadow-rose-500/25"
                icon={<Zap className="w-6 h-6" />}
              >
                Start Your Story Today
              </EnhancedButton>

              <div className="mt-6">
                <EnhancedButton 
                  onClick={() => navigate('/subscription/plans')}
                  variant="outline"
                  size="lg"
                  className="border-purple-400/50 text-purple-300 hover:bg-purple-400/10 backdrop-blur-sm"
                  icon={<Star className="w-5 h-5 text-yellow-300" />}
                >
                  ✨ Upgrade to Premium
                </EnhancedButton>
              </div>
            </div>

            {/* PWA Settings Link */}
            <div className="mt-16 pt-8 border-t border-white/10 animate-fadeInUp stagger-4">
              <button
                onClick={() => navigate('/pwa-settings')}
                className="text-gray-500 hover:text-rose-400 text-sm transition-colors flex items-center mx-auto hover-lift font-light tracking-wide"
              >
                <span>App Settings</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedHomepage;